import React from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import BrandMark from '../components/BrandMark';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import RequestItemEditor from '../components/RequestItemEditor';

// Option VALUES stay in English (that's what saves to the database, so the
// admin dashboard reads consistently) — only the visible labels translate.
const SERVICE_TYPES = ['Handyman – General Repair', 'Handyman – Carpentry', 'Handyman – Drywall', 'Handyman – Painting', 'Handyman – Fence / Deck', 'Remodel – Kitchen', 'Remodel – Bathroom', 'Basement Finish', 'Other'];
const URGENCIES = ['Standard – 1 week', 'Soon – 2-3 days', 'Urgent – 24 hrs'];
// QPS always promises a TWO HOUR window, never "afternoon" — so the form
// offers the same six windows the text intake narrows people down to
// (server/intake.js WINDOWS). Keep the two lists in step.
const BEST_TIMES = ['8-10 am', '10 am-12 pm', '12-2 pm', '2-4 pm', '4-6 pm', '6-8 pm', 'Any time'];

export { SERVICE_TYPES };

export default function RequestForm() {
  const {
    currentScreen, currentUser, goTo,
    formData, handleInputChange,
    requestItems, setRequestItems, newRequestItem,
    editingRequestItem, setEditingRequestItem,
    isSubmitting, handleSubmit,
  } = useApp();
  const { t, optLabel } = useI18n();

  const photoCount = requestItems.reduce((n, it) => n + (it.files?.length || 0), 0);

  return (
    <div className={`screen ${currentScreen === 'screen-request' ? 'active' : ''}`}>
      <nav className="nav-public">
        <BrandMark />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LanguageSwitcher />
          <button className="btn btn-ghost btn-sm" onClick={() => goTo(currentUser?.role === 'homeowner' ? 'screen-homeowner-dashboard' : 'screen-home')}>{t('nav.back')}</button>
        </div>
      </nav>
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} className="pb">
        <div className="section-title">{t('req.title')}</div>
        <div className="section-sub">{t('req.sub')}</div>
        <div className="alert alert-blue">{t('req.photoTip')}</div>

        <form onSubmit={handleSubmit}>
          {/* Project first — the request leads with their job, not their info.
              And it is a LIST: the leak, the gate that won't latch and the hole
              in the drywall are three jobs in one visit, and typing them into
              one box is how one of them gets forgotten between the estimate and
              the morning the tech turns up. A line each, with its own photos,
              carries all the way through to what they approve. */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>{t('req.jobDetails')}</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5, marginBottom: 10 }}>{t('req.itemsHint')}</div>

          {requestItems.map((it, i) => {
            const shots = it.files?.length || 0;
            return (
              <button type="button" key={i} className="item-row" onClick={() => setEditingRequestItem(i)}>
                <span style={{ minWidth: 0 }}>
                  {/* display:block locally rather than in the shared CSS —
                      the tech's estimate rows use the same classes and are not
                      this screen's to restyle. */}
                  <span className="item-row-t" style={{ display: 'block' }}>{it.title || it.description || t('req.untitledItem')}</span>
                  <span className="item-row-s" style={{ display: 'block' }}>
                    {it.serviceType ? optLabel(it.serviceType) : t('req.noServiceType')}
                    {shots > 0 && ` · ${shots} ${t('req.attachments').toLowerCase()}`}
                  </span>
                </span>
                <span className="item-row-amt" style={{ color: 'var(--blue)' }}>›</span>
              </button>
            );
          })}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }} onClick={() => {
            setRequestItems([...requestItems, newRequestItem()]);
            setEditingRequestItem(requestItems.length);
          }}>+ {t('req.addItem')}</button>

          <div className="form-row">
            <div className="form-group"><label>{t('req.urgency')}</label><select name="urgency" value={formData.urgency} onChange={handleInputChange}>{URGENCIES.map(u => <option key={u} value={u}>{optLabel(u)}</option>)}</select></div>
            <div className="form-group"><label>{t('req.bestTime')}</label><select name="bestTime" value={formData.bestTime} onChange={handleInputChange}>{BEST_TIMES.map(b => <option key={b} value={b}>{optLabel(b)}</option>)}</select></div>
          </div>

          {/* Photos belong to a line now, not to the request, so this is the
              count and a way back to the line that still needs one. The rule
              itself has not changed: nothing attached, no estimate worth
              sending. A PDF counts — a property manager forwarding a move-out
              inspection has given us more to price from than three snapshots,
              and refusing it would only teach them to email instead. */}
          <div className="form-group">
            <label>{t('req.photosVideos')} <span className="req">*</span></label>
            <div className="upload-zone" style={{ cursor: 'default' }}>
              {photoCount > 0 ? (
                <p style={{ fontSize: 13, color: 'var(--slate)' }}>
                  {t('req.photosOnItems').replace('{n}', photoCount)}
                  <br /><span style={{ fontSize: 12, color: 'var(--muted)' }}>{t('req.photosTapItem')}</span>
                </p>
              ) : (
                <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {t('req.photosTapItem')}
                  <br /><span style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>{t('req.atLeast1')}</span>
                </p>
              )}
            </div>
          </div>

          <div className="divider"></div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>{t('req.serviceLocation')}</div>
          <div className="form-group"><label>{t('req.serviceAddress')} <span className="req">*</span></label><input type="text" name="serviceAddress" value={formData.serviceAddress} onChange={handleInputChange} placeholder="123 Main St" required /></div>
          <div className="form-group"><label>{t('req.city')} <span className="req">*</span></label>
            <select name="city" value={formData.city} onChange={handleInputChange} required>
              <option value="">{t('req.selectCity')}</option>
              {BRAND.serviceArea.primaryCities.map(c => <option key={c} value={c}>{c}</option>)}
              <option value="Other">{t('req.otherNotListed')}</option>
            </select>
          </div>
          {formData.city === 'Other' && (
            <div className="form-group"><label>{t('req.whichCity')} <span className="req">*</span></label><input type="text" name="cityOther" value={formData.cityOther} onChange={handleInputChange} placeholder={t('req.enterCity')} required /></div>
          )}
          <div className="form-group"><label>{t('req.zip')} <span className="req">*</span></label><input type="text" name="zip" inputMode="numeric" maxLength={10} value={formData.zip} onChange={handleInputChange} placeholder="84043" required /></div>
          <label className="same-check"><input type="checkbox" name="billingSame" checked={formData.billingSame} onChange={handleInputChange} /> {t('req.billingSame')}</label>

          <div className="divider"></div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>{t('req.contact')}</div>
          {currentUser ? (
            <div className="alert alert-blue" style={{ marginBottom: 14 }}>{t('req.submittingAs')} <strong>{currentUser.name}</strong> ({currentUser.email})</div>
          ) : (
            <>
              <div className="form-group"><label>{t('req.fullName')} <span className="req">*</span></label><input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Jane Doe" required /></div>
              <div className="form-row">
                <div className="form-group"><label>{t('req.phone')} <span className="req">*</span></label><input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(801) 555-0100" required /></div>
                <div className="form-group"><label>{t('req.email')} <span className="req">*</span></label><input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="jane@email.com" required /></div>
              </div>
            </>
          )}

          {/* Express written consent to job texts. Carriers audit this: the box
              must be unchecked by default, must not be required to buy anything,
              and the disclosure must name the sender, the message type, that
              rates may apply, and how to stop. Deliberately NOT translated —
              the legal pages it links to are English-only. */}
          <div className="divider"></div>
          <label className="same-check" style={{ alignItems: 'flex-start', lineHeight: 1.55 }}>
            <input type="checkbox" name="smsConsent" checked={formData.smsConsent} onChange={handleInputChange} style={{ marginTop: 3 }} />
            <span style={{ fontSize: 12.5 }}>
              Text me about my job. I agree to receive text messages from {BRAND.name.full} about this
              service request — scheduling, technician arrival, job updates, and repairs needing my approval.
              Message frequency varies. Message and data rates may apply. Reply STOP to opt out, HELP for
              help. Consent is not a condition of purchase. See our{' '}
              <span className="form-link" style={{ display: 'inline' }} onClick={(e) => { e.preventDefault(); goTo('screen-privacy'); }}>Privacy Policy</span>
              {' '}and{' '}
              <span className="form-link" style={{ display: 'inline' }} onClick={(e) => { e.preventDefault(); goTo('screen-terms'); }}>Terms of Service</span>.
            </span>
          </label>
          <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 14px' }}>
            Optional. Leave it unchecked and we'll reach you by phone and email instead.
          </p>

          <button type="submit" className="btn btn-primary btn-lg" disabled={isSubmitting} style={{ marginBottom: 10 }}>
            {isSubmitting ? t('req.submitting') : t('req.submit')}
          </button>
          <button type="button" className="btn btn-ghost btn-lg" onClick={() => goTo(currentUser?.role === 'homeowner' ? 'screen-homeowner-dashboard' : 'screen-home')}>{t('req.cancel')}</button>
          <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', marginTop: 10 }}><span style={{ color: 'var(--red)' }}>*</span> {t('req.requiredFields')}</p>
        </form>
      </div>

      {/* One line of the request, on its own screen (components/RequestItemEditor). */}
      {editingRequestItem !== null && requestItems[editingRequestItem] && (
        <RequestItemEditor
          item={requestItems[editingRequestItem]}
          index={editingRequestItem}
          total={requestItems.length}
          serviceTypes={SERVICE_TYPES}
          onChange={(next) => {
            const items = [...requestItems];
            items[editingRequestItem] = next;
            setRequestItems(items);
          }}
          onDelete={() => {
            const items = requestItems.filter((_, i) => i !== editingRequestItem);
            setRequestItems(items.length ? items : [newRequestItem()]);
            setEditingRequestItem(null);
          }}
          onClose={() => setEditingRequestItem(null)}
        />
      )}
    </div>
  );
}
